import React, { useEffect, useMemo, useState } from 'react';
import { withStyles, Button, LinearProgress } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import classnames from 'classnames';
import DatePicker from '../../../components/DatePicker';
import { Autocomplete } from '../../../components/Autocomplete';

// SERVICES
import { getClientListSelectVM, getSelfInspectOutcomeData, getSelfInspectOutcomeExcel } from '../../../services';

const styles = theme => ({
  datePicker: {
    padding: 10,
  },
  clientSelect: {
    minWidth: 320,
    marginLeft: 20,
  },
  section: {
    marginTop: 24,
    width: '100%',
  },
  tableContainer: {
    maxHeight: 600,
  },
  headerCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    backgroundColor: '#f5f5f5',
  },
  totalRow: {
    '& td': {
      fontWeight: 600,
      backgroundColor: '#fafafa',
    },
  },
});

// summary columns shown on the page, the Excel download contains every column
const SUMMARY_COLUMNS = [
  { key: 'ClientName', label: 'Client' },
  { key: 'ReportCode', label: 'Report Code' },
  { key: 'TotalImported', label: 'Imported' },
  { key: 'InviteSent', label: 'Invite Sent' },
  { key: 'InviteFailed', label: 'Invite Failed' },
  { key: 'OptedOutTotal', label: 'Opted Out' },
  { key: 'PreviouslyOptedOut', label: 'Previously Opted Out' },
  { key: 'OptedOutAfterInvite', label: 'Opted Out After Invite' },
  { key: 'InvalidPhoneTotal', label: 'Invalid Phone' },
  { key: 'PhoneMissing', label: 'Phone Missing' },
  { key: 'PhoneInvalid', label: 'Phone Unverifiable' },
  { key: 'PhoneNotMobile', label: 'Not Mobile' },
  { key: 'AgentContacted', label: 'Agent Contacted' },
  { key: 'AgentCorrectedPhone', label: 'Agent Corrected' },
  { key: 'Abandoned', label: 'Abandoned' },
  { key: 'ManualReassign', label: 'Manual Reassign' },
  { key: 'ManualConvert', label: 'Manual Convert' },
  { key: 'ConvertedTotal', label: 'Converted' },
  { key: 'ConvertedToField', label: 'To Field' },
  { key: 'Cancelled', label: 'Cancelled' },
  { key: 'ConvertedToVirtual', label: 'To Virtual' },
  { key: 'ConvertedToProduct', label: 'To Other Product' },
  { key: 'CompletedWithoutConversion', label: 'Completed (No Conversion)' },
];

// detail flag columns rolled up into a single readable list
const DETAIL_FLAGS = [
  { key: 'InviteSent', label: 'Invite sent' },
  { key: 'InviteFailed', label: 'Invite failed' },
  { key: 'PhoneMissing', label: 'Phone missing' },
  { key: 'PhoneInvalid', label: 'Phone unverifiable' },
  { key: 'PhoneNotMobile', label: 'Not mobile' },
  { key: 'PreviouslyOptedOut', label: 'Previously opted out' },
  { key: 'OptedOut', label: 'Opted out' },
  { key: 'OptedIn', label: 'Opted in' },
  { key: 'AgentContacted', label: 'Agent contacted' },
  { key: 'AgentPhoneInvalid', label: 'Agent phone invalid' },
  { key: 'AgentPhoneUpdated', label: 'Agent corrected phone' },
  { key: 'Abandoned', label: 'Abandoned' },
  { key: 'ManualReassign', label: 'Manual reassign' },
  { key: 'ManualConvert', label: 'Manual convert' },
];

const DETAIL_COLUMNS = [
  { key: 'ClientName', label: 'Client' },
  { key: 'ReportCode', label: 'Report Code' },
  { key: 'OriginalOrderLineId', label: 'Original Order' },
  { key: 'CurrentOrderLineId', label: 'Current Order' },
  { key: 'CurrentReportCode', label: 'Current Report Code' },
  { key: 'CurrentStatus', label: 'Current Status' },
  { key: 'InsuredName', label: 'Insured' },
  { key: 'InsuredPhone', label: 'Phone' },
  { key: 'ImportedOn', label: 'Imported', format: 'date' },
  { key: 'Events', label: 'Events' },
  { key: 'ConversionType', label: 'Conversion' },
  { key: 'ConversionReason', label: 'Conversion Reason' },
  { key: 'ConversionDetail', label: 'Conversion Detail' },
  { key: 'ConvertedOnUtc', label: 'Converted', format: 'utcDate' },
];

// the api returns { columns: [], rows: [[]] } so map rows to objects keyed by column name
const toObjects = ({ columns = [], rows = [] } = {}) =>
  rows.map(row => columns.reduce((obj, column, i) => ({ ...obj, [column]: row[i] }), {}));

const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (format === 'date') {
    return new Date(value).toLocaleString();
  }
  if (format === 'utcDate') {
    const utc = typeof value === 'string' && !value.endsWith('Z') ? `${value}Z` : value;
    return new Date(utc).toLocaleString();
  }
  return value;
};

export const SelfInspectOutcomes = withStyles(styles)(props => {
  const { classes } = props;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clientList, setClientList] = useState([]);
  const [clientListLoading, setClientListLoading] = useState(false);
  const [selectedClientOpt, setSelectedClientOpt] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const datesMissing = !startDate || !endDate;
  const dateRangeInvalid = !datesMissing && startDate > endDate;
  const formInvalid = datesMissing || dateRangeInvalid;
  const validationMessage = dateRangeInvalid ? 'Start date must be on or before the end date.' : '';

  useEffect(() => {
    const getClientListAsync = async () => {
      try {
        setClientListLoading(true);
        const clients = await getClientListSelectVM();
        setClientList(
          clients.map(client => ({
            value: client.clientId,
            label: `${client.clientName}${client.clientCode ? ` (${client.clientCode})` : ''}`,
          })),
        );
      } catch (error) {
        //
      }
      setClientListLoading(false);
    };

    getClientListAsync();
  }, []);

  // clear results when the filters change so the page never shows data for different filters
  useEffect(() => {
    setReport(null);
  }, [startDate, endDate, selectedClientOpt]);

  const filters = () => ({ startDate, endDate, clientId: selectedClientOpt?.value });

  const runReportAsync = async () => {
    setError('');
    setReport(null);
    setPage(0);

    try {
      setIsLoading(true);
      const data = await getSelfInspectOutcomeData(filters());
      const summary = toObjects(data?.summary);
      const detail = toObjects(data?.detail).map(row => ({
        ...row,
        Events: DETAIL_FLAGS.filter(flag => row[flag.key] === 1)
          .map(flag => flag.label)
          .join(', '),
      }));

      if (!detail.length) {
        setError('There are no self inspect orders for the selected filters.');
        return;
      }

      setReport({ summary, detail });
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAsync = async () => {
    setError('');

    try {
      setIsDownloading(true);
      await getSelfInspectOutcomeExcel(filters());
    } catch (error) {
      setError(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const pagedDetail = useMemo(
    () => (report ? report.detail.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : []),
    [report, page, rowsPerPage],
  );

  const renderTable = (columns, rows, isTotalRow = () => false) => (
    <TableContainer className={classes.tableContainer}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.key} className={classes.headerCell}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} className={classnames({ [classes.totalRow]: isTotalRow(row) })}>
              {columns.map(column => (
                <TableCell key={column.key}>{formatValue(row[column.key], column.format)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className={classnames('flex-1', 'col-start-center', 'align-stretch', 'card', 'border-left-green')}>
      <h3 style={{ marginBottom: 12 }}>
        Self inspect outcomes for orders imported in the selected date range, including opt outs, invalid phone numbers
        and orders that were converted.
      </h3>
      <p style={{ marginTop: 0, marginBottom: 24, color: '#666' }}>
        Orders that were converted and replaced by a new order are traced back to the originally imported order.
      </p>

      <div className={classnames('flex-col')}>
        <div className={classnames('row-between-center')}>
          <div className={classnames('row-center-center')}>
            <div className={classnames('col-start-start')} style={{ marginRight: 10 }}>
              <span className={classnames('text-left')}>From:</span>
              <DatePicker selected={startDate} onChange={setStartDate} className={classes.datePicker} />
            </div>
            <div className={classnames('col-start-start')} style={{ marginLeft: 10 }}>
              <span className={classnames('text-left')}>Through:</span>
              <DatePicker selected={endDate} onChange={setEndDate} className={classes.datePicker} />
            </div>
            <div className={classnames('col-start-start', classes.clientSelect)}>
              <span className={classnames('text-left')}>Client:</span>
              <Autocomplete
                value={selectedClientOpt}
                loading={clientListLoading}
                placeholder="All clients"
                options={clientList}
                onChange={opt => setSelectedClientOpt(opt)}
                isClearable
              />
            </div>
          </div>

          <div className={classnames('row-center-center')}>
            <Button
              variant="contained"
              style={{ backgroundColor: formInvalid ? '#aaa' : 'var(--jmi-blue)', color: 'white', marginLeft: 24 }}
              onClick={runReportAsync}
              disabled={formInvalid || isLoading || isDownloading}
            >
              Run Report
            </Button>
            <Button
              variant="outlined"
              style={{ marginLeft: 12 }}
              onClick={downloadAsync}
              disabled={formInvalid || isLoading || isDownloading}
            >
              Download Excel
            </Button>
          </div>
        </div>

        <div style={{ width: '100%', marginTop: 24 }}>
          <div style={{ visibility: isLoading || isDownloading ? 'visible' : 'hidden' }}>
            <LinearProgress />
          </div>
          <div style={{ color: 'red' }}>{error || validationMessage}</div>
        </div>
      </div>

      {report && (
        <>
          <div className={classes.section}>
            <h4>Summary</h4>
            {renderTable(SUMMARY_COLUMNS, report.summary, row => row.ClientName === 'TOTAL')}
          </div>

          <div className={classes.section}>
            <h4>Orders ({report.detail.length})</h4>
            {renderTable(DETAIL_COLUMNS, pagedDetail)}
            <TablePagination
              component="div"
              count={report.detail.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              onChangePage={(e, newPage) => setPage(newPage)}
              onChangeRowsPerPage={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
});
